// Per-screen header bar — type glyph + id, title, subtitle, pills, metric tiles, actions.
// Replicates the design's `ObjectHeader` from screens-misc.jsx / screen-bid.jsx.

import type { ReactNode } from 'react';
import { Glyph, type GlyphType } from './Glyph.js';
import { Pill, type Tone } from './Pill.js';

interface PillSpec {
  tone?: Tone;
  label: string;
}
interface MetricTile {
  k: string;
  v: string | number;
  tone?: 'pri' | 'grn' | 'amb' | 'rd';
}

export function ObjectHeader({
  type,
  id,
  title,
  sub,
  pills = [],
  metrics = [],
  actions,
}: {
  type: GlyphType;
  id?: string;
  title: string;
  sub?: string;
  pills?: PillSpec[];
  metrics?: MetricTile[];
  actions?: ReactNode;
}) {
  return (
    <div
      style={{
        height: 72,
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        borderBottom: '1px solid var(--bd)',
        background: 'var(--bg-pane)',
        flexShrink: 0,
      }}
    >
      <Glyph type={type} size={32} />

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span
            className="meta mono"
            style={{
              fontSize: 10,
              color: 'var(--fg-3)',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            }}
          >
            {type}
            {id ? ` · ${id}` : ''}
          </span>
          {pills.map((p, i) =>
            p.tone ? (
              <Pill key={i} tone={p.tone} dot>
                {p.label}
              </Pill>
            ) : (
              <Pill key={i} dot>
                {p.label}
              </Pill>
            ),
          )}
        </div>
        <div
          className="title-l"
          style={{
            marginTop: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </div>
        {sub && (
          <div
            className="meta mono"
            style={{
              marginTop: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {sub}
          </div>
        )}
      </div>

      {metrics.length > 0 && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
          {metrics.map((m, i) => (
            <div
              key={i}
              style={{
                paddingLeft: 12,
                borderLeft: i === 0 ? '1px solid var(--bd)' : '1px solid var(--bd-soft)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minWidth: 80,
              }}
            >
              <div className="lbl">{m.k}</div>
              <div className={`metric${m.tone ? ` ${m.tone}` : ''}`}>{String(m.v)}</div>
            </div>
          ))}
        </div>
      )}

      {actions && <div style={{ display: 'flex', gap: 6, marginLeft: 12 }}>{actions}</div>}
    </div>
  );
}
